import type {
  IRatingMatrixFrameworkParameter,
  IRatingMatrixFrameworkSubParameter,
} from '__generated__';

export function buildParameterTemplateFilter(templateId: number): string {
  return `frameworkTemplateID==${templateId}`;
}

export function getNextSortOrder(items: { sortOrder: number }[]): number {
  if (items.length === 0) {
    return 1;
  }

  return Math.max(...items.map((item) => item.sortOrder)) + 1;
}

function sortSubsForMerge(subs: IRatingMatrixFrameworkSubParameter[]): IRatingMatrixFrameworkSubParameter[] {
  return [...subs].sort((left, right) => {
    const leftGroup = left.groupSortOrder ?? 0;
    const rightGroup = right.groupSortOrder ?? 0;

    if (leftGroup !== rightGroup) {
      return leftGroup - rightGroup;
    }

    return left.sortOrder - right.sortOrder;
  });
}

export function mergeParametersWithSubParameters(
  parameters: IRatingMatrixFrameworkParameter[],
  subParameters: IRatingMatrixFrameworkSubParameter[],
): IRatingMatrixFrameworkParameter[] {
  const subsByParameter = new Map<number, IRatingMatrixFrameworkSubParameter[]>();

  subParameters.forEach((sub) => {
    const list = subsByParameter.get(sub.parameterID) ?? [];
    list.push(sub);
    subsByParameter.set(sub.parameterID, list);
  });

  return parameters.map((parameter) => {
    const subs = sortSubsForMerge(subsByParameter.get(parameter.id) ?? []);

    return {
      ...parameter,
      subParameters: subs,
    };
  });
}

export function weightFractionToPercent(fraction: number): number {
  return Number((fraction * 100).toFixed(2));
}

export function formatWeightPercent(fraction: number): string {
  return String(weightFractionToPercent(fraction));
}

export function parseWeightPercent(percent: number): number {
  return percent / 100;
}

export function collectCategoryTitles(parameters: IRatingMatrixFrameworkParameter[]): string[] {
  const titles = new Set<string>();

  parameters.forEach((parameter) => {
    if (parameter.categoryTitle?.trim()) {
      titles.add(parameter.categoryTitle.trim());
    }
  });

  return [...titles].sort();
}

export function computeParamsSummary(parameters: IRatingMatrixFrameworkParameter[]) {
  const paramsCount = parameters.length;
  const subsCount = parameters.reduce(
    (sum, parameter) => sum + (parameter.subParameters?.length ?? 0),
    0,
  );
  const totalWeight = parameters.reduce((sum, parameter) => sum + (parameter.weight ?? 0), 0);

  return {
    paramsCount,
    subsCount,
    totalWeightPercent: weightFractionToPercent(totalWeight),
  };
}

export function countDistinctGroups(subParameters: IRatingMatrixFrameworkSubParameter[]): number {
  const groups = new Set<string>();

  subParameters.forEach((sub) => {
    if (sub.groupTitle?.trim()) {
      groups.add(sub.groupTitle.trim());
    }
  });

  return groups.size;
}

export type WeightImpact = {
  beforePercent: number;
  afterPercent: number;
  othersPercent: number;
  projectedTotalPercent: number;
};

export function computeWeightImpact(
  parameters: IRatingMatrixFrameworkParameter[],
  excludeId?: number,
  projectedWeightFraction?: number,
): WeightImpact {
  const beforePercent = weightFractionToPercent(
    parameters.reduce((sum, parameter) => sum + (parameter.weight ?? 0), 0),
  );

  const remaining = parameters.filter((parameter) => parameter.id !== excludeId);
  const afterPercent = weightFractionToPercent(
    remaining.reduce((sum, parameter) => sum + (parameter.weight ?? 0), 0),
  );

  const othersPercent = weightFractionToPercent(
    parameters
      .filter((parameter) => parameter.id !== excludeId)
      .reduce((sum, parameter) => sum + (parameter.weight ?? 0), 0),
  );

  const projectedTotalPercent =
    projectedWeightFraction != null
      ? weightFractionToPercent(
          parameters
            .filter((parameter) => parameter.id !== excludeId)
            .reduce((sum, parameter) => sum + (parameter.weight ?? 0), 0) + projectedWeightFraction,
        )
      : afterPercent;

  return { beforePercent, afterPercent, othersPercent, projectedTotalPercent };
}

export type CascadeSummary = {
  subCount: number;
  rangeSetCount: number;
  optionSetCount: number;
};

export function computeDeleteCascadeForParameter(
  parameter: IRatingMatrixFrameworkParameter,
  isQuantitative: (type: IRatingMatrixFrameworkSubParameter['measurementType']) => boolean,
  isQualitative: (type: IRatingMatrixFrameworkSubParameter['measurementType']) => boolean,
): CascadeSummary {
  const subs = parameter.subParameters ?? [];
  let rangeSetCount = 0;
  let optionSetCount = 0;

  subs.forEach((sub) => {
    if (isQuantitative(sub.measurementType)) {
      rangeSetCount += 1;
    }
    if (isQualitative(sub.measurementType)) {
      optionSetCount += 1;
    }
  });

  return {
    subCount: subs.length,
    rangeSetCount,
    optionSetCount,
  };
}
